/**
 * AddArticleToCollectionDropdown Component
 *
 * A button that opens a search dropdown for adding journal articles to a user
 * collection. The user types a query; matching articles are listed, and
 * clicking a result immediately links that article to the collection (there is
 * no selection/remove step inside the dropdown — each click is an add).
 *
 * Articles already in the collection are not offered, and an article added
 * during this session is marked as added and becomes non-interactive so it
 * cannot be linked twice.
 */

import { useCallback, useEffect, useMemo, useState, type JSX } from "react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Check, Loader2, Plus, Search } from "lucide-react"
import {
  getJournalArticles,
  type JournalArticle,
} from "@/services/journalArticlesService"
import { addSourcesToCollection } from "@/services/biblioService"
import { useAuth } from "@/hooks/useAuth"

/** How many search results to request/show at once. */
const SEARCH_RESULT_LIMIT: number = 10
/** Debounce delay (ms) before a typed query triggers a search. */
const SEARCH_DEBOUNCE_MS: number = 300

/** Props for the AddArticleToCollectionDropdown component. */
interface AddArticleToCollectionDropdownProps {
  /** The collection id that articles are added to. */
  collectionId: number
  /** The collection's display name, used in labels. */
  collectionName: string
  /**
   * Source ids already in this collection. These are excluded from the search
   * results so an article is never offered for a duplicate add.
   */
  existingSourceIds: number[]
  /**
   * Called after an article has been successfully added, so the parent can
   * refresh the collection's article list.
   *
   * @param sourceId - The source id that was added.
   */
  onArticleAdded: (sourceId: number) => void | Promise<void>
}

/**
 * A search dropdown for adding journal articles to a collection on click.
 *
 * @param props - Component props.
 * @param props.collectionId - The collection id to add articles to.
 * @param props.collectionName - The collection's display name.
 * @param props.existingSourceIds - Source ids already in the collection.
 * @param props.onArticleAdded - Callback invoked after a successful add.
 * @returns The rendered button + search dropdown.
 */
export default function AddArticleToCollectionDropdown({
  collectionId,
  collectionName,
  existingSourceIds,
  onArticleAdded,
}: AddArticleToCollectionDropdownProps): JSX.Element {
  const { getAccessToken } = useAuth()

  /** Whether the dropdown is open. */
  const [open, setOpen] = useState<boolean>(false)
  /** The raw text in the search input. */
  const [query, setQuery] = useState<string>("")
  /** The articles returned by the latest search. */
  const [results, setResults] = useState<JournalArticle[]>([])
  /** Whether a search request is in flight. */
  const [searching, setSearching] = useState<boolean>(false)
  /** Error message shown when a search fails. */
  const [searchError, setSearchError] = useState<string | null>(null)
  /** The source id currently being added (its row shows a spinner). */
  const [addingSourceId, setAddingSourceId] = useState<number | null>(null)
  /** Error message shown when an add fails. */
  const [addError, setAddError] = useState<string | null>(null)
  /** Source ids added during this open session, marked as done in the list. */
  const [addedSourceIds, setAddedSourceIds] = useState<Set<number>>(new Set())

  /** Set form of the collection's existing ids for O(1) exclusion lookups. */
  const existingIdSet = useMemo<Set<number>>(
    () => new Set(existingSourceIds),
    [existingSourceIds]
  )

  // Debounced search: wait until the user pauses typing before hitting the API.
  // A trimmed empty query clears the results instead of searching for nothing.
  useEffect(() => {
    if (!open) {
      return
    }

    const trimmed: string = query.trim()
    if (trimmed.length === 0) {
      setResults([])
      setSearching(false)
      setSearchError(null)
      return
    }

    let cancelled: boolean = false
    setSearching(true)
    setSearchError(null)

    const timer = setTimeout(async () => {
      try {
        const token: string = await getAccessToken()
        const response = await getJournalArticles(token, 1, SEARCH_RESULT_LIMIT, {
          searchTerm: trimmed,
        })
        if (!cancelled) {
          setResults(response.items)
        }
      } catch (err) {
        if (!cancelled) {
          const message: string =
            err instanceof Error ? err.message : "Failed to search articles"
          setSearchError(message)
          setResults([])
        }
      } finally {
        if (!cancelled) {
          setSearching(false)
        }
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query, open, getAccessToken])

  // Reset transient state each time the dropdown closes so it opens clean.
  useEffect(() => {
    if (open) {
      return
    }
    setQuery("")
    setResults([])
    setSearching(false)
    setSearchError(null)
    setAddingSourceId(null)
    setAddError(null)
    setAddedSourceIds(new Set())
  }, [open])

  /**
   * Add a single article to the collection, then notify the parent. The row is
   * marked as added on success so it cannot be clicked again.
   *
   * @param article - The article whose source id is linked to the collection.
   * @returns A promise that resolves once the add attempt has completed.
   */
  const handleAddArticle = useCallback(
    async (article: JournalArticle): Promise<void> => {
      if (addingSourceId !== null) {
        return
      }
      setAddingSourceId(article.sourceId)
      setAddError(null)
      try {
        const token: string = await getAccessToken()
        await addSourcesToCollection({
          idToken: token,
          collectionId,
          sourceIds: [article.sourceId],
        })
        setAddedSourceIds((prev) => {
          const next = new Set(prev)
          next.add(article.sourceId)
          return next
        })
        await onArticleAdded(article.sourceId)
      } catch (err) {
        const message: string =
          err instanceof Error ? err.message : "Failed to add article"
        setAddError(message)
      } finally {
        setAddingSourceId(null)
      }
    },
    [addingSourceId, getAccessToken, collectionId, onArticleAdded]
  )

  /**
   * The articles to show: search results minus any already in the collection.
   * Articles added during this session remain visible (marked as added) so the
   * user gets clear confirmation rather than having rows silently disappear.
   */
  const visibleResults = useMemo<JournalArticle[]>(
    () =>
      results.filter(
        (article) =>
          !existingIdSet.has(article.sourceId) ||
          addedSourceIds.has(article.sourceId)
      ),
    [results, existingIdSet, addedSourceIds]
  )

  const trimmedQuery: string = query.trim()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={`Add an article to collection ${collectionName}`}
        >
          <Plus className="size-3.5" />
          Add article
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 gap-2 p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, DOI, PubMed ID, or source ID…"
            aria-label="Search journal articles"
            className="h-8 pl-8 text-sm"
            autoFocus
          />
        </div>

        {/* Add-side error (a click-to-add failed). */}
        {addError !== null && (
          <p className="text-xs text-destructive" role="alert">
            {addError}
          </p>
        )}

        {/* Fixed-height, scrollable results list. */}
        <div className="max-h-72 overflow-y-auto" role="listbox" aria-label="Search results">
          {searching ? (
            <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              <span>Searching…</span>
            </div>
          ) : searchError !== null ? (
            <p className="px-2 py-3 text-xs text-destructive" role="alert">
              {searchError}
            </p>
          ) : trimmedQuery.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">
              Start typing to search for articles to add.
            </p>
          ) : visibleResults.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">
              No matching articles found.
            </p>
          ) : (
            <ul className="flex flex-col">
              {visibleResults.map((article) => {
                const isAdded: boolean = addedSourceIds.has(article.sourceId)
                const isAdding: boolean = addingSourceId === article.sourceId
                return (
                  <li key={article.sourceId}>
                    <button
                      type="button"
                      disabled={isAdded || isAdding}
                      onClick={() => handleAddArticle(article)}
                      className="flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent disabled:pointer-events-none disabled:opacity-60"
                      aria-label={
                        isAdded
                          ? `${article.title || "Untitled article"} added`
                          : `Add ${article.title || "Untitled article"}`
                      }
                    >
                      <span className="mt-0.5 shrink-0">
                        {isAdding ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : isAdded ? (
                          <Check className="size-3.5 text-green-600 dark:text-green-500" />
                        ) : (
                          <Plus className="size-3.5 text-muted-foreground" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-2">
                          {article.title || "Untitled article"}
                        </span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {[
                            article.publicationYear || null,
                            `ID ${article.sourceId}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
